import { type MediaErrorCode } from '@vidstack/react'

import { Button, Icon, Text, VStack } from '@masterplandev/ui'

import useTranslation from '@/core/hooks/useTranslation'

interface Props {
  code?: MediaErrorCode
  onRetry: () => void
}

/**
 * Overlay shown in place of the player content when media playback fails.
 *
 * Positioned against `[data-media-player]`, which Vidstack's theme already
 * makes `position: relative`.
 */
export function MediaPlayerError({ code, onRetry }: Props) {
  const { translate } = useTranslation('media-player')

  // Code 4 is the library/resource load failure; 1-3 and an absent code are
  // playback failures and must not be mislabelled as a load problem.
  const description =
    code === 4
      ? translate('error.description-load-failed')
      : translate('error.description-generic')

  return (
    <VStack
      role="alert"
      data-testid="media-player-error"
      position="absolute"
      inset={0}
      zIndex={1}
      bg="mp2.neutralLight.100"
      justify="center"
      px={6}>
      <Icon symbol="alerts-alert-circle" size="md" color="mp2.error.600" />
      <Text
        fontSize="inherit"
        textAlign="center"
        lineHeight={1.1}
        color="mp2.neutralDark.100"
        textStyle="button">
        {translate('error.title')}
      </Text>
      <Text
        data-testid="media-player-error-description"
        fontSize="xs"
        lineHeight={1.1}
        color="mp2.neutralDark.100"
        textAlign="center">
        {description}
      </Text>
      <Button
        data-testid="media-player-error-retry"
        onClick={onRetry}
        size="sm">
        {translate('error.retry')}
      </Button>
    </VStack>
  )
}
